import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Package, MapPin, Heart, LogOut, Loader2, Plus, Trash2, RotateCcw } from "lucide-react";
import { shopApi, formatPrice, formatApiError } from "@/lib/api";
import { useCustomer } from "@/context/CustomerContext";
import { useCart } from "@/context/CartContext";
import OrderTimeline from "@/components/OrderTimeline";
import ProductCard from "@/components/ProductCard";
import { AirplaneLogo } from "@/components/motifs";
import { toast } from "sonner";

function ReturnBox({ order }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [type, setType] = useState("return");
  const [done, setDone] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error("Please add a reason"); return; }
    try {
      await shopApi.post("/customer/returns", { order_id: order.id, product_name: order.items?.[0]?.name || "", reason, type });
      setDone(true); toast.success("Return/exchange requested");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not submit"); }
  };
  if (done) return <p className="mt-3 text-xs text-green-700" data-testid={`return-done-${order.order_number}`}>Request submitted — our team will be in touch.</p>;
  return (
    <div className="mt-3 border-t border-[#EFE8DC] pt-3">
      <button data-testid={`return-toggle-${order.order_number}`} onClick={() => setOpen(!open)} className="text-xs text-amber-700 underline">Request return / exchange</button>
      {open && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <select data-testid={`return-type-${order.order_number}`} value={type} onChange={(e) => setType(e.target.value)} className="rounded-lg border border-[#E7DFD3] px-2 py-1.5 text-xs">
            <option value="return">Return</option><option value="exchange">Exchange</option>
          </select>
          <input data-testid={`return-reason-${order.order_number}`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" className="min-w-[160px] flex-1 rounded-lg border border-[#E7DFD3] px-3 py-1.5 text-xs outline-none focus:border-amber-500" />
          <button data-testid={`return-submit-${order.order_number}`} onClick={submit} className="rounded-full gold-gradient px-3 py-1.5 text-xs font-medium text-stone-900">Submit</button>
        </div>
      )}
    </div>
  );
}

const STATUSES = ["Order Confirmed", "Packed", "Dispatched", "In Transit", "Out for Delivery", "Delivered"];

function AuthForm() {
  const { login, register } = useCustomer();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    const res = mode === "login" ? await login(f.email, f.password) : await register(f);
    if (!res.ok) setErr(res.error);
    setBusy(false);
  };

  return (
    <div className="mx-auto max-w-md px-4 py-16">
      <div className="rounded-2xl border border-[#E7DFD3] bg-white p-8 shadow-sm">
        <div className="flex flex-col items-center">
          <AirplaneLogo size={44} />
          <h1 className="mt-3 font-serif text-3xl text-stone-900">{mode === "login" ? "Welcome Back" : "Join PRIYARAA"}</h1>
          <p className="mt-1 text-sm text-stone-500">Your first-class jewellery journey awaits.</p>
        </div>
        <div className="mt-6 flex rounded-full border border-[#E7DFD3] p-1">
          {["login", "register"].map((m) => (
            <button key={m} data-testid={`auth-tab-${m}`} onClick={() => { setMode(m); setErr(""); }}
              className={`flex-1 rounded-full py-2 text-sm font-medium uppercase tracking-wider transition-colors ${mode === m ? "gold-gradient text-stone-900" : "text-stone-500"}`}>
              {m === "login" ? "Login" : "Register"}
            </button>
          ))}
        </div>
        <form onSubmit={submit} className="mt-6 space-y-3" data-testid="customer-auth-form">
          {mode === "register" && (
            <input data-testid="reg-name" required placeholder="Full name" value={f.name} onChange={(e) => set("name", e.target.value)} className="w-full rounded-lg border border-[#E7DFD3] px-3 py-2.5 outline-none focus:border-amber-500" />
          )}
          <input data-testid="auth-email" required type="email" placeholder="Email" value={f.email} onChange={(e) => set("email", e.target.value)} className="w-full rounded-lg border border-[#E7DFD3] px-3 py-2.5 outline-none focus:border-amber-500" />
          {mode === "register" && (
            <input data-testid="reg-phone" placeholder="Phone (optional)" value={f.phone} onChange={(e) => set("phone", e.target.value)} className="w-full rounded-lg border border-[#E7DFD3] px-3 py-2.5 outline-none focus:border-amber-500" />
          )}
          <input data-testid="auth-password" required type="password" placeholder="Password" value={f.password} onChange={(e) => set("password", e.target.value)} className="w-full rounded-lg border border-[#E7DFD3] px-3 py-2.5 outline-none focus:border-amber-500" />
          {err && <p className="text-sm text-red-600" data-testid="auth-error">{err}</p>}
          <button data-testid="auth-submit" disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-full gold-gradient py-3 font-semibold uppercase tracking-wider text-stone-900 disabled:opacity-60">
            {busy && <Loader2 size={16} className="animate-spin" />} {mode === "login" ? "Login" : "Create Account"}
          </button>
        </form>
      </div>
    </div>
  );
}

function AddressForm({ onSaved }) {
  const [f, setF] = useState({ label: "Home", full_name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "", is_default: false });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    try { const { data } = await shopApi.post("/customer/addresses", f); onSaved(data); toast.success("Address saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="mt-4 grid grid-cols-2 gap-3 rounded-xl border border-[#E7DFD3] p-4" data-testid="address-form">
      <input placeholder="Full name" data-testid="addr-name" value={f.full_name} onChange={(e) => set("full_name", e.target.value)} className="rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
      <input placeholder="Phone" data-testid="addr-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
      <input placeholder="Address line 1" data-testid="addr-line1" value={f.line1} onChange={(e) => set("line1", e.target.value)} className="col-span-2 rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
      <input placeholder="City" data-testid="addr-city" value={f.city} onChange={(e) => set("city", e.target.value)} className="rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
      <input placeholder="Pincode" data-testid="addr-pincode" value={f.pincode} onChange={(e) => set("pincode", e.target.value)} className="rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
      <button data-testid="addr-save" onClick={save} className="col-span-2 rounded-full gold-gradient py-2.5 text-sm font-semibold text-stone-900">Save address</button>
    </div>
  );
}

export default function Account() {
  const { user, logout, loading } = useCustomer();
  const { addItem, setOpen } = useCart();
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [showAddr, setShowAddr] = useState(false);

  useEffect(() => {
    if (!user) return;
    shopApi.get("/customer/orders").then(({ data }) => setOrders(data));
    shopApi.get("/customer/addresses").then(({ data }) => setAddresses(data));
    shopApi.get("/customer/wishlist").then(({ data }) => setWishlist(data));
  }, [user, tab]);

  if (loading) return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="animate-spin text-amber-600" /></div>;
  if (!user) return <AuthForm />;

  const reorder = (order) => {
    order.items.forEach((it) => addItem({ id: it.product_id, name: it.name, price: it.price, image_url: it.image_url }, it.qty));
    toast.success("Items added to bag");
    setOpen(true);
  };

  const TABS = [["orders", "Orders", Package], ["wishlist", "Wishlist", Heart], ["addresses", "Addresses", MapPin], ["profile", "Profile", User]];

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl text-stone-900">Hi, {user.name?.split(" ")[0] || "Traveller"} ✈️</h1>
          <p className="text-sm text-stone-500">{user.email}</p>
        </div>
        <button data-testid="logout-button" onClick={logout} className="flex items-center gap-2 rounded-full border border-[#E7DFD3] px-4 py-2 text-sm text-stone-600 hover:border-amber-500">
          <LogOut size={15} /> Logout
        </button>
      </div>

      <div className="mt-6 flex gap-2 overflow-x-auto border-b border-[#E7DFD3]">
        {TABS.map(([k, label, Icon]) => (
          <button key={k} data-testid={`account-tab-${k}`} onClick={() => setTab(k)}
            className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${tab === k ? "border-amber-500 text-amber-700" : "border-transparent text-stone-500"}`}>
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "orders" && (
          <div className="space-y-5" data-testid="orders-list">
            {orders.length === 0 && <p className="text-stone-500">No orders yet. <button onClick={() => navigate("/shop")} className="text-amber-700 underline">Start shopping</button></p>}
            {orders.map((o) => (
              <div key={o.id} data-testid={`order-${o.order_number}`} className="rounded-2xl border border-[#E7DFD3] bg-white p-5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-mono text-sm text-stone-900">{o.order_number}</p>
                    <p className="text-xs text-stone-500">{new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })} • {o.items.length} item(s) • {formatPrice(o.total)}</p>
                  </div>
                  <button data-testid={`reorder-${o.order_number}`} onClick={() => reorder(o)} className="flex items-center gap-1.5 rounded-full border border-[#E7DFD3] px-3 py-1.5 text-xs text-stone-600 hover:border-amber-500">
                    <RotateCcw size={13} /> Reorder
                  </button>
                </div>
                {o.status !== "Cancelled" && (
                  <div className="mt-6 px-2"><OrderTimeline statuses={STATUSES} current={o.status} timeline={o.timeline} /></div>
                )}
                <ReturnBox order={o} />
              </div>
            ))}
          </div>
        )}

        {tab === "wishlist" && (
          <div data-testid="wishlist-grid">
            {wishlist.length === 0 ? <p className="text-stone-500">Your wishlist is empty.</p> : (
              <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
                {wishlist.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
            )}
          </div>
        )}

        {tab === "addresses" && (
          <div data-testid="addresses-list">
            <div className="space-y-3">
              {addresses.map((a) => (
                <div key={a.id} className="flex items-start justify-between rounded-xl border border-[#E7DFD3] bg-white p-4">
                  <div>
                    <p className="font-medium text-stone-900">{a.full_name} {a.is_default && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] uppercase text-amber-700">Default</span>}</p>
                    <p className="text-sm text-stone-500">{a.line1}, {a.city} - {a.pincode} • {a.phone}</p>
                  </div>
                  <button onClick={async () => { const { data } = await shopApi.delete(`/customer/addresses/${a.id}`); setAddresses(data); }} className="text-stone-400 hover:text-red-600"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
            {showAddr ? <AddressForm onSaved={(d) => { setAddresses(d); setShowAddr(false); }} /> : (
              <button data-testid="add-address-button" onClick={() => setShowAddr(true)} className="mt-4 flex items-center gap-2 rounded-full border border-[#E7DFD3] px-4 py-2 text-sm text-stone-600 hover:border-amber-500"><Plus size={15} /> Add address</button>
            )}
          </div>
        )}

        {tab === "profile" && (
          <div className="max-w-md space-y-3" data-testid="profile-panel">
            <div className="rounded-xl border border-[#E7DFD3] bg-white p-4"><p className="text-xs text-stone-400">Name</p><p className="text-stone-900">{user.name}</p></div>
            <div className="rounded-xl border border-[#E7DFD3] bg-white p-4"><p className="text-xs text-stone-400">Email</p><p className="text-stone-900">{user.email}</p></div>
            <div className="rounded-xl border border-[#E7DFD3] bg-white p-4"><p className="text-xs text-stone-400">Phone</p><p className="text-stone-900">{user.phone || "—"}</p></div>
          </div>
        )}
      </div>
    </div>
  );
}
